/**
 * Replays a finished run's transcript against the game content to reconstruct, step by step, what the
 * player chose on every scenario and how each decision moved the four meters — the data behind the
 * answer-by-answer review (admin oversight + the player's own debrief).
 *
 * Pure (no React/XState) and deliberately mirrors machine.ts scoring exactly (applyDelta / popupDelta),
 * so the reconstructed running totals match what the player actually saw. The transcript only records
 * the chosen option id (+ tier) / pop-up ranking; everything else (option text, reaction, per-meter
 * delta, principle) is looked up from the content here.
 */
import type { Choice, ScoreDelta, Scenario } from "@patc/content-schema";
import { gameContent } from "../content";
import type { TranscriptEntry } from "./machine";
import {
  applyDelta,
  initialScores,
  popupBandDistance,
  popupDelta,
  type PopupRankEntry,
  type Scores,
} from "./scoring";

const ZERO_DELTA: ScoreDelta = {
  dealSecurity: 0,
  clientSatisfaction: 0,
  counterpartyCooperation: 0,
  transactionProcess: 0,
};
const SCENARIO_BY_ID = new Map<string, Scenario>(gameContent.scenarios.map((s) => [s.id, s]));

/** A standard scenario (choice / knowledge-check) the player answered. */
export interface DecisionStep {
  n: number; // 1-based position in the run
  kind: "choice" | "knowledge_check";
  scenarioId: string;
  phase: Scenario["phase"];
  title: string;
  prompt: string; // the issue / question posed
  choice: Choice | null; // the option the player picked (null if it can't be resolved)
  /** The model (best-tier) option + its transferable principle, for the "what was right" disclosure. */
  model: { text: string; tier: Choice["tier"]; principle?: string } | null;
  delta: ScoreDelta;
  scoresAfter: Scores;
}

/** One ranked task in the prioritisation pop-up, with how the player placed it vs its accepted band. */
export interface PopupTaskRow {
  taskId: string;
  text: string;
  placed: number; // where the player ranked it (1 = first; 0 = not placed)
  band: readonly [number, number]; // accepted band [lo, hi]
  offBy: number; // positions outside the band (0 = within)
}
export interface PopupStep {
  n: number;
  kind: "popup";
  scenarioId: string;
  phase: Scenario["phase"];
  title: string;
  prompt: string;
  rows: PopupTaskRow[];
  offByTotal: number;
  /** The model triage rationale (the "what was right" disclosure for the pop-up). */
  debrief?: string;
  delta: ScoreDelta;
  scoresAfter: Scores;
}
export type ReplayStep = DecisionStep | PopupStep;

export interface ReplayResult {
  steps: ReplayStep[];
  /** Running totals after the last step (should equal the run's stored final scores). */
  finalScores: Scores;
}

/** Replay a transcript into per-step deltas + running totals, starting from the content's initial scores. */
export function replayRun(transcript: TranscriptEntry[]): ReplayResult {
  let scores = initialScores(gameContent);
  const steps: ReplayStep[] = [];
  let n = 0;

  for (const entry of transcript ?? []) {
    const sc = SCENARIO_BY_ID.get(entry.scenarioId);
    if (!sc) continue; // unknown scenario id — skip defensively
    n += 1;

    if (sc.kind === "popup") {
      const ranking: PopupRankEntry[] = entry.ranking ?? [];
      const delta = popupDelta(sc, ranking);
      scores = applyDelta(scores, delta, gameContent);
      const placedBy = new Map(ranking.map((r) => [r.taskId, r.rank]));
      const rows: PopupTaskRow[] = sc.tasks.map((t) => {
        const placed = placedBy.get(t.id) ?? 0;
        const [lo, hi] = t.correctBand;
        const offBy = placed === 0 ? 0 : placed < lo ? lo - placed : placed > hi ? placed - hi : 0;
        return { taskId: t.id, text: t.text, placed, band: t.correctBand, offBy };
      });
      steps.push({
        n,
        kind: "popup",
        scenarioId: sc.id,
        phase: sc.phase,
        title: sc.title,
        prompt: sc.prompt,
        rows,
        offByTotal: popupBandDistance(sc, ranking),
        debrief: sc.debrief,
        delta,
        scoresAfter: scores,
      });
    } else {
      const choice = sc.choices.find((c) => c.id === entry.choiceId) ?? null;
      const delta = choice?.score ?? ZERO_DELTA;
      scores = applyDelta(scores, delta, gameContent);
      const prompt = sc.kind === "choice" ? sc.issue : (sc.question ?? sc.description);
      const tierRank: Record<Choice["tier"], number> = { best: 0, good: 1, weak: 2, worst: 3 };
      const best = [...sc.choices].sort((a, b) => tierRank[a.tier] - tierRank[b.tier])[0] ?? null;
      steps.push({
        n,
        kind: sc.kind,
        scenarioId: sc.id,
        phase: sc.phase,
        title: sc.title ?? sc.id,
        prompt,
        choice,
        model: best ? { text: best.text, tier: best.tier, principle: best.principle } : null,
        delta,
        scoresAfter: scores,
      });
    }
  }

  return { steps, finalScores: scores };
}
